if (!customElements.get('mega-nav-tabs')) {
  class MegaNavTabs extends HTMLElement {
    connectedCallback() {
      this.addEventListener('click', this.handleClick.bind(this));
      this.addEventListener('focusin', this.handleFocus.bind(this));

      // The tablist sits inside a <details class="js-mega-nav"> that's shown
      // via a has-motion opacity/visibility transition rather than the
      // native open/closed display toggle (see main.js's hover handling) —
      // so its box already has real dimensions before it's actually visible,
      // and the native `toggle` event (fired the moment the disclosure opens
      // or closes, on both the hover and tap/click paths — either one ends
      // up setting/removing the `open` attribute) is a more direct, immediate
      // trigger to re-measure than waiting on a ResizeObserver callback.
      // ResizeObserver stays too, purely for the *narrower* case of the
      // viewport itself changing width while the menu is already open (a
      // plain `toggle` listener can't catch that, since nothing toggles).
      const tablist = this.querySelector('.mega-nav-tabs__tablist');
      const details = this.closest('details');
      if (tablist) {
        if (details) {
          details.addEventListener('toggle', () => this.checkTablistOverflow(tablist));
        }
        new ResizeObserver(() => this.checkTablistOverflow(tablist)).observe(tablist);
      }

      this.prevArrow = this.querySelector('.mega-nav-tabs__tablist-arrow--prev');
      this.nextArrow = this.querySelector('.mega-nav-tabs__tablist-arrow--next');
      if (tablist && this.prevArrow && this.nextArrow) {
        this.prevArrow.addEventListener('click', () => this.scrollTablist(tablist, -1));
        this.nextArrow.addEventListener('click', () => this.scrollTablist(tablist, 1));
      }
    }

    // Whether the tabs actually wrap to a second row, checked directly (do
    // any two tabs sit at different offsetTop values) rather than inferred
    // from scrollWidth vs clientWidth — that comparison only means anything
    // once flex-wrap is already `nowrap`, which is exactly the thing this
    // class toggle decides in the first place (see this class's own comment
    // in navigation-mega-tabs.css).
    checkTablistOverflow(tablist) {
      const tabs = tablist.querySelectorAll('.mega-nav-tabs__tab');
      if (tabs.length < 2) return;
      const firstTop = tabs[0].offsetTop;
      const wraps = Array.from(tabs).some((tab) => tab.offsetTop !== firstTop);
      tablist.classList.toggle('is-overflowing', wraps);
    }

    scrollTablist(tablist, direction) {
      tablist.scrollBy({ left: direction * tablist.clientWidth * 0.8, behavior: 'smooth' });
    }

    handleClick(evt) {
      const tab = evt.target.closest('.mega-nav-tabs__tab');
      if (!tab || !this.contains(tab)) return;

      // Flat quick links have no matching panel. Leave them as normal links on
      // every device instead of intercepting the tap as an in-place tab switch.
      if (tab.classList.contains('mega-nav-tabs__tab--quicklink')) return;

      // Desktop with a real pointer: MainMenu's own mouseenter listener
      // (handleSidenavMenuToggle, wired to every .js-sidebar-hover element
      // including these tabs) already previews on hover, so a click is left
      // to navigate normally. The same click also bubbles to
      // MainMenu.handleNavClick (preventDefault doesn't stop propagation),
      // but its .closest('summary')/.js-toggle checks don't match a bare
      // tab <a>, so there's no double-handling either way.
      if (theme.mediaMatches.md && theme.device.hasHover) return;

      // Respect modifier/middle clicks (open in new tab/window) even on
      // touch/narrow viewports, where every *other* click on this tab gets
      // preventDefault()'d below to intercept it into a panel switch —
      // without this check, Cmd/Ctrl/Shift+click on a tab silently got
      // swallowed into the same in-place toggle instead of opening a new
      // tab, which is real <a> behaviour this handler otherwise preserves
      // everywhere else. evt.button is almost always 0 here regardless
      // (middle-click fires 'auxclick', not 'click', in every evergreen
      // browser) but checked anyway rather than assumed.
      if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button !== 0) return;

      // Touch (any width) and narrow/no-hover viewports: there's no hover to
      // preview with, and this tab is a bare <a> with none of <summary>'s
      // built-in non-navigating disclosure behavior — so without this,
      // tapping anything but the already-visible tab just navigates away.
      // Always preventDefault in this branch (even when re-tapping the
      // already-active tab) so a second tap never sneaks past into a
      // navigation; only skip the actual class toggle when there's nothing
      // to change.
      evt.preventDefault();
      this.activateTab(tab);
    }

    // Keyboard parity for desktop: MainMenu's mouseenter listener (main.js)
    // previews a tab's panel on hover, but never on focus, so a sighted
    // keyboard-only user tabbing through these was getting no preview at
    // all — Enter just navigated straight to whichever tab was focused,
    // the same way a click does, with nothing shown in between. This
    // mirrors mouseenter's own effect (just the .is-visible toggle, no
    // navigation) so tabbing through now previews the same way hovering
    // does, and Enter still navigates exactly as before. Scoped to
    // :focus-visible specifically so a click's own incidental focus
    // (already handled by handleClick, and about to navigate anyway on
    // this device class) doesn't also run this redundantly, and gated to
    // the same desktop-with-hover device class handleClick's "let it
    // navigate" branch uses — touch has no keyboard-focus-without-a-tap in
    // the first place, so this parity gap doesn't exist there.
    handleFocus(evt) {
      const tab = evt.target.closest('.mega-nav-tabs__tab');
      if (!tab || !this.contains(tab)) return;
      if (!(theme.mediaMatches.md && theme.device.hasHover)) return;
      if (!tab.matches(':focus-visible')) return;
      this.activateTab(tab);
    }

    // Only the tab's own class needs flipping — the matching panel is no
    // longer a CSS *sibling* of its tab (see .mega-nav-tabs__tablist in
    // sections/header.liquid), but navigation-mega-tabs.css derives which
    // panel to show from the visible tab's *position* among its siblings
    // via :has(), so this one class toggle is all any caller needs to do —
    // none of them have to know the panel exists at all.
    activateTab(tab) {
      if (tab.classList.contains('is-visible')) return;
      this.querySelectorAll('.mega-nav-tabs__tab').forEach((el) => {
        const active = el === tab;
        el.classList.toggle('is-visible', active);
        el.setAttribute('aria-expanded', String(active));
      });
    }
  }

  customElements.define('mega-nav-tabs', MegaNavTabs);
}
